import {
  AfterViewInit,
  Directive,
  ElementRef,
  Injector,
  OnDestroy,
  Renderer2,
  runInInjectionContext,
} from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { OverflowTooltipDirective, TooltipOptions } from '@tpt/common/tooltips/overflow';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const OVERFLOW_TOOLTIP_OPTIONS: Partial<TooltipOptions> = {
  mode: 'white-background',
  static: false,
};

const INPUT_VALUE_TEXT_SELECTOR = '.k-input-value-text';
const LIST_ITEM_TEXT_SELECTOR = '.k-list-item-text';

type OverflowTooltipFactory = () => OverflowTooltipDirective;

function createOverflowTooltipDirective(injector: Injector): OverflowTooltipDirective {
  return runInInjectionContext(injector, () => {
    const factory = OverflowTooltipDirective.ɵfac as unknown as OverflowTooltipFactory;
    return factory();
  });
}

@Directive({
  standalone: true,
  selector: 'kendo-dropdownlist[tiKendoDropDownTooltip]',
})
export class KendoDropDownTooltipDirective implements AfterViewInit, OnDestroy {
  private readonly destroyed = new Subject<void>();
  private readonly attachedDirectives = new Map<HTMLElement, OverflowTooltipDirective>();
  private inputObserver?: MutationObserver;
  private inputRefreshFrame?: number;

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly component: DropDownListComponent,
    private readonly injector: Injector,
    private readonly renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    this.refreshInputValueTooltip();
    this.observeInputValue();

    this.component.valueChange.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.refreshInputValueTooltip();
    });

    this.component.opened.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.attachListItemTooltips();
    });

    this.component.closed.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.hideAllTooltips();
    });
  }

  ngOnDestroy(): void {
    this.inputObserver?.disconnect();
    if (this.inputRefreshFrame !== undefined) {
      cancelAnimationFrame(this.inputRefreshFrame);
    }

    for (const [element, directive] of this.attachedDirectives) {
      directive.ngOnDestroy();
      this.attachedDirectives.delete(element);
    }

    this.destroyed.next();
    this.destroyed.complete();
  }

  // The `.k-input-inner` wrapper and its value text are rendered asynchronously, and
  // programmatic / ngModel value changes never emit `valueChange`. Observe the host element
  // so the tooltip title stays in sync with whatever text Kendo renders, whenever it renders.
  private observeInputValue(): void {
    this.inputObserver = new MutationObserver(() => this.scheduleInputValueRefresh());
    this.inputObserver.observe(this.element.nativeElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // Coalesce bursts of DOM mutations into a single refresh per frame, so re-rendering a page
  // full of dropdowns doesn't run the refresh once per mutation.
  private scheduleInputValueRefresh(): void {
    if (this.inputRefreshFrame !== undefined) {
      return;
    }

    this.inputRefreshFrame = requestAnimationFrame(() => {
      this.inputRefreshFrame = undefined;
      this.refreshInputValueTooltip();
    });
  }

  private refreshInputValueTooltip(): void {
    this.cleanupDetachedDirectives();

    // `.k-input-value-text` is the element that clips the value (its `overflow: hidden` makes
    // `min-width: auto` resolve to 0, so it shrinks and ellipsises itself). Kendo re-creates
    // this node on every value change, hence the MutationObserver re-runs this to re-attach.
    const span = this.element.nativeElement.querySelector(INPUT_VALUE_TEXT_SELECTOR);
    this.attachOverflowTooltip(span);
  }

  private attachListItemTooltips(): void {
    this.cleanupDetachedDirectives();

    // List items render after `opened` fires, so wait for the next frame before reading them.
    requestAnimationFrame(() => {
      const items = this.component.optionsList?.items?.toArray() ?? [];
      items.forEach((item) => {
        const span = item.element.nativeElement.querySelector(LIST_ITEM_TEXT_SELECTOR);
        this.attachOverflowTooltip(span);
      });
    });
  }

  private attachOverflowTooltip(element: Element | null): void {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    this.setTitle(element);

    if (this.attachedDirectives.has(element)) {
      return;
    }

    const childInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: ElementRef, useValue: new ElementRef(element) }],
    });

    const directive = createOverflowTooltipDirective(childInjector);
    directive.tiOverflowTooltip = OVERFLOW_TOOLTIP_OPTIONS;
    directive.ngAfterViewInit();
    this.attachedDirectives.set(element, directive);
  }

  private setTitle(element: HTMLElement): void {
    this.renderer.setAttribute(element, 'title', element.textContent?.trim() ?? '');
  }

  private hideAllTooltips(): void {
    for (const directive of this.attachedDirectives.values()) {
      directive.hide();
    }
  }

  private cleanupDetachedDirectives(): void {
    for (const [element, directive] of this.attachedDirectives) {
      if (!element.isConnected) {
        directive.ngOnDestroy();
        this.attachedDirectives.delete(element);
      }
    }
  }
}
